import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Paper, Snackbar, Stack } from "@mui/material";
import { useAuth } from "../../../../context/AuthContext";
import ProductCatalogSelectors from "./ProductCatalogSelectors";
import ProductDetailsForm from "./ProductDetailsForm";
import ProductVariantsEditor from "./ProductVariantsEditor";
import ProductImagesUploader from "./ProductImagesUploader";

const initialForm = {
  section_uuid: "",
  category_uuid: "",
  brand_uuid: "",
  name: "",
  description: "",
  base_price: "",
  has_offer: false,
  offer_price: "",
  iva_percentage: 15,
  stock: 0,
  stock_minimum: 0,
  ecommerce: true,
  is_active: true,
};

const normalizeDecimalInput = (value) => {
  return String(value ?? "").replace(/,/g, ".");
};

const normalizeId = (value) => {
  return value === null || value === undefined ? "" : String(value);
};

const calcFinalPrice = (price, iva) => {
  const numericPrice = Number(normalizeDecimalInput(price) || 0);
  const numericIva = Number(iva || 0);

  if (!numericPrice || numericIva === 999) return numericPrice.toFixed(2);

  const total = numericPrice + (numericPrice * numericIva) / 100;
  return total.toFixed(2);
};

export default function ProductEditorPanel({ product, onSuccess }) {
  const { authFetch } = useAuth();

  const [form, setForm] = useState(initialForm);
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [variants, setVariants] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [primaryImageKey, setPrimaryImageKey] = useState("");
  const [deleteImageIds, setDeleteImageIds] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState({
    type: "",
    message: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "error",
    message: "",
  });

  useEffect(() => {
    const loadCatalog = async () => {
      setLoadingCatalog(true);
      setBanner({ type: "", message: "" });

      try {
        const [sectionsRes, categoriesRes, brandsRes] = await Promise.all([
          authFetch("/api/store/sections/"),
          authFetch("/api/store/categories/"),
          authFetch("/api/store/brands/"),
        ]);

        const [sectionsData, categoriesData, brandsData] = await Promise.all([
          sectionsRes.json().catch(() => []),
          categoriesRes.json().catch(() => []),
          brandsRes.json().catch(() => []),
        ]);

        if (!sectionsRes.ok || !categoriesRes.ok || !brandsRes.ok) {
          setBanner({
            type: "error",
            message: "No se pudieron cargar los datos del catálogo",
          });
          return;
        }

        setSections(Array.isArray(sectionsData) ? sectionsData : []);
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        setBrands(Array.isArray(brandsData) ? brandsData : []);
      } catch {
        setBanner({
          type: "error",
          message: "Ocurrió un error al cargar el catálogo",
        });
      } finally {
        setLoadingCatalog(false);
      }
    };

    loadCatalog();
  }, [authFetch]);

  useEffect(() => {
    if (product) {
      const sectionUuid = normalizeId(product.section_uuid);
      const categoryUuid = normalizeId(product.category_uuid);
      const hasOffer = String(product.offer_price || "").trim() !== "";

      setForm({
        section_uuid: sectionUuid,
        category_uuid: categoryUuid,
        brand_uuid: normalizeId(product.brand_uuid),
        name: product.name || "",
        description: product.description || "",
        base_price: normalizeDecimalInput(product.base_price || ""),
        has_offer: hasOffer,
        offer_price: hasOffer ? normalizeDecimalInput(product.offer_price || "") : "",
        iva_percentage: product.iva_percentage ?? 15,
        stock: product.stock ?? 0,
        stock_minimum: product.stock_minimum ?? 0,
        ecommerce: typeof product.ecommerce === "boolean" ? product.ecommerce : true,
        is_active: typeof product.is_active === "boolean" ? product.is_active : true,
      });

      setVariants(
        Array.isArray(product.variants)
          ? product.variants.map((item) => ({
              uuid: item.uuid,
              variant_type: item.variant_type || "",
              variant_value: item.variant_value || "",
              price: normalizeDecimalInput(item.price || ""),
              iva_percentage: item.iva_percentage ?? 15,
              stock: item.stock ?? 0,
              is_active: typeof item.is_active === "boolean" ? item.is_active : true,
            }))
          : []
      );

      const existing = Array.isArray(product.images)
        ? product.images.map((img) => ({
            uuid: img.uuid,
            url: img.url,
            is_primary: img.is_primary,
          }))
        : [];

      setExistingImages(existing);
      setNewImages([]);

      const primaryExisting = existing.find((img) => img.is_primary);
      setPrimaryImageKey(primaryExisting ? `existing-${primaryExisting.uuid}` : "");
      setDeleteImageIds([]);
    } else {
      setForm(initialForm);
      setVariants([]);
      setExistingImages([]);
      setNewImages([]);
      setPrimaryImageKey("");
      setDeleteImageIds([]);
    }

    setBanner({ type: "", message: "" });
  }, [product]);

  const filteredCategories = useMemo(() => {
    if (!form.section_uuid) return [];
    return categories.filter(
      (item) => normalizeId(item.section_uuid) === normalizeId(form.section_uuid)
    );
  }, [categories, form.section_uuid]);

  useEffect(() => {
    if (loadingCatalog) return;
    if (!form.section_uuid) return;
    if (!form.category_uuid) return;

    const exists = filteredCategories.some(
      (item) => normalizeId(item.uuid) === normalizeId(form.category_uuid)
    );

    if (!exists) {
      setForm((prev) => ({
        ...prev,
        category_uuid: "",
      }));
    }
  }, [loadingCatalog, filteredCategories, form.section_uuid, form.category_uuid]);

  const visibleExistingImages = useMemo(() => {
    return existingImages.filter((img) => !deleteImageIds.includes(img.uuid));
  }, [existingImages, deleteImageIds]);

  const allVisibleImageKeys = useMemo(() => {
    const existingKeys = visibleExistingImages.map((img) => `existing-${img.uuid}`);
    const newKeys = newImages.map((img) => img.key);
    return [...existingKeys, ...newKeys];
  }, [visibleExistingImages, newImages]);

  useEffect(() => {
    if (!allVisibleImageKeys.length) {
      setPrimaryImageKey("");
      return;
    }

    if (!allVisibleImageKeys.includes(primaryImageKey)) {
      setPrimaryImageKey(allVisibleImageKeys[0]);
    }
  }, [allVisibleImageKeys, primaryImageKey]);

  const selectedVariantType = useMemo(() => {
    const firstWithType = variants.find((item) => String(item.variant_type || "").trim());
    return firstWithType ? firstWithType.variant_type : "";
  }, [variants]);

  const handleFormChange = (field, value) => {
    let nextValue = value;

    if (["base_price", "offer_price"].includes(field)) {
      nextValue = normalizeDecimalInput(value);
    }

    if (["stock", "stock_minimum"].includes(field)) {
      nextValue = String(value ?? "").replace(/,/g, ".");
    }

    setForm((prev) => {
      const next = {
        ...prev,
        [field]: nextValue,
      };

      if (field === "has_offer" && !nextValue) {
        next.offer_price = "";
      }

      if (field === "section_uuid" && normalizeId(prev.section_uuid) !== normalizeId(nextValue)) {
        next.category_uuid = "";
      }

      return next;
    });
  };

  const handleAddVariant = () => {
    setVariants((prev) => [
      ...prev,
      {
        local_id: `${Date.now()}-${Math.random()}`,
        variant_type: selectedVariantType || "",
        variant_value: "",
        price: "",
        iva_percentage: Number(form.iva_percentage) || 15,
        stock: 0,
        is_active: true,
      },
    ]);
  };

  const handleUpdateVariant = (key, field, value) => {
    let nextValue = value;

    if (field === "price") {
      nextValue = normalizeDecimalInput(value);
    }

    if (field === "stock") {
      nextValue = String(value ?? "").replace(/,/g, ".");
    }

    setVariants((prev) =>
      prev.map((item) => {
        const itemKey = item.uuid || item.local_id;
        if (itemKey !== key) return item;
        return {
          ...item,
          [field]: nextValue,
        };
      })
    );
  };

  const handleRemoveVariant = (key) => {
    setVariants((prev) =>
      prev.filter((item) => {
        const itemKey = item.uuid || item.local_id;
        return itemKey !== key;
      })
    );
  };

  const handleAddImages = (files) => {
    const mapped = files.map((file, index) => ({
      key: `new-${Date.now()}-${index}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
    }));

    setNewImages((prev) => [...prev, ...mapped]);

    if (!primaryImageKey && mapped.length) {
      setPrimaryImageKey(mapped[0].key);
    }
  };

  const handleRemoveExistingImage = (uuid) => {
    setDeleteImageIds((prev) => {
      if (prev.includes(uuid)) return prev;
      return [...prev, uuid];
    });
  };

  const handleRestoreExistingImage = (uuid) => {
    setDeleteImageIds((prev) => prev.filter((item) => item !== uuid));
  };

  const handleRemoveNewImage = (key) => {
    setNewImages((prev) => {
      const target = prev.find((item) => item.key === key);
      if (target?.preview) {
        URL.revokeObjectURL(target.preview);
      }
      return prev.filter((item) => item.key !== key);
    });
  };

  const buildPrimaryImageIndex = () => {
    const existingKeys = visibleExistingImages.map((img) => `existing-${img.uuid}`);
    const newKeys = newImages.map((img) => img.key);
    const combined = [...existingKeys, ...newKeys];

    if (!combined.length) return "";

    const index = combined.findIndex((item) => item === primaryImageKey);
    return index >= 0 ? String(index) : "0";
  };

  const showSnackbar = (type, message) => {
    setSnackbar({
      open: true,
      type,
      message,
    });
  };

  const validateVariants = () => {
    if (!variants.length) return true;

    const normalizedType = selectedVariantType ? String(selectedVariantType).trim() : "";

    for (const item of variants) {
      const type = String(item.variant_type || "").trim();
      const value = String(item.variant_value || "").trim();
      const price = String(item.price || "").trim();

      if (!type && !value && !price && Number(item.stock || 0) === 0) {
        showSnackbar("error", "Hay una variante vacía. Complétala o elimínala.");
        return false;
      }

      if (!type) {
        showSnackbar("error", "Todas las variantes deben tener un tipo.");
        return false;
      }

      if (!value) {
        showSnackbar("error", "Todas las variantes deben tener un valor.");
        return false;
      }

      if (price === "") {
        showSnackbar("error", "Todas las variantes deben tener precio.");
        return false;
      }

      if (normalizedType && type !== normalizedType) {
        showSnackbar("error", "Todas las variantes deben usar el mismo tipo.");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!form.section_uuid) {
      setBanner({ type: "error", message: "Selecciona una sección" });
      showSnackbar("error", "Selecciona una sección");
      return;
    }

    if (!form.category_uuid) {
      setBanner({ type: "error", message: "Selecciona una categoría" });
      showSnackbar("error", "Selecciona una categoría");
      return;
    }

    if (!form.brand_uuid) {
      setBanner({ type: "error", message: "Selecciona una marca" });
      showSnackbar("error", "Selecciona una marca");
      return;
    }

    if (!String(form.name || "").trim()) {
      setBanner({ type: "error", message: "Ingresa el nombre del producto" });
      showSnackbar("error", "Ingresa el nombre del producto");
      return;
    }

    if (String(form.base_price || "").trim() === "") {
      setBanner({ type: "error", message: "Ingresa el precio base" });
      showSnackbar("error", "Ingresa el precio base");
      return;
    }

    if (form.has_offer && String(form.offer_price || "").trim() === "") {
      setBanner({ type: "error", message: "Ingresa el precio de oferta" });
      showSnackbar("error", "Ingresa el precio de oferta");
      return;
    }

    if (!validateVariants()) {
      return;
    }

    setLoading(true);
    setBanner({ type: "", message: "" });

    try {
      const url = product
        ? `/api/store/products/${product.uuid}/update/`
        : "/api/store/products/create/";

      const method = product ? "PUT" : "POST";
      const formData = new FormData();

      formData.append("section_uuid", normalizeId(form.section_uuid));
      formData.append("category_uuid", normalizeId(form.category_uuid));
      formData.append("brand_uuid", normalizeId(form.brand_uuid));
      formData.append("name", String(form.name || "").trim());
      formData.append("description", String(form.description || "").trim());
      formData.append("base_price", normalizeDecimalInput(form.base_price));
      formData.append(
        "offer_price",
        form.has_offer ? normalizeDecimalInput(form.offer_price) : ""
      );
      formData.append("iva_percentage", String(form.iva_percentage));
      formData.append("stock", String(form.stock ?? "").replace(/,/g, "."));
      formData.append("stock_minimum", String(form.stock_minimum ?? "").replace(/,/g, "."));
      formData.append("ecommerce", String(Boolean(form.ecommerce)));
      formData.append("is_active", String(Boolean(form.is_active)));

      formData.append(
        "variants",
        JSON.stringify(
          variants.map((item) => {
            const payload = {
              variant_type: String(item.variant_type || "").trim(),
              variant_value: String(item.variant_value || "").trim(),
              price: normalizeDecimalInput(item.price),
              iva_percentage: Number(item.iva_percentage),
              stock: String(item.stock ?? "").replace(/,/g, "."),
              is_active: Boolean(item.is_active),
            };

            if (item.uuid) {
              payload.uuid = item.uuid;
            }

            return payload;
          })
        )
      );

      if (product) {
        formData.append("delete_image_ids", JSON.stringify(deleteImageIds));
      }

      newImages.forEach((item) => {
        formData.append("images", item.file);
      });

      const primaryIndex = buildPrimaryImageIndex();
      if (primaryIndex !== "") {
        formData.append("primary_image_index", primaryIndex);
      }

      const response = await authFetch(url, {
        method,
        body: formData,
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setBanner({
          type: "error",
          message: data?.error || "No se pudo guardar el producto",
        });
        showSnackbar("error", data?.error || "No se pudo guardar el producto");
        return;
      }

      onSuccess(
        data?.message ||
          (product ? "Producto actualizado correctamente" : "Producto creado correctamente")
      );
    } catch {
      setBanner({
        type: "error",
        message: "Ocurrió un error al guardar el producto",
      });
      showSnackbar("error", "Ocurrió un error al guardar el producto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={1.8}>
      {banner.message && (
        <Alert
          severity={banner.type}
          sx={{
            borderRadius: "12px",
            bgcolor:
              banner.type === "error"
                ? "rgba(220,38,38,0.12)"
                : "rgba(34,197,94,0.12)",
            color:
              banner.type === "error"
                ? "#dc2626"
                : "#166534",
            border: `1px solid ${
              banner.type === "error"
                ? "rgba(220,38,38,0.18)"
                : "rgba(34,197,94,0.18)"
            }`,
            "& .MuiAlert-icon": {
              color:
                banner.type === "error"
                  ? "#dc2626"
                  : "#166534",
            },
          }}
        >
          {banner.message}
        </Alert>
      )}

      <ProductCatalogSelectors
        form={form}
        sections={sections}
        categories={filteredCategories}
        brands={brands}
        onChange={handleFormChange}
        loading={loadingCatalog}
      />

      <ProductDetailsForm
        form={form}
        onChange={handleFormChange}
        loading={loading}
        finalPrice={calcFinalPrice(form.base_price, form.iva_percentage)}
        offerFinalPrice={
          form.has_offer && String(form.offer_price || "").trim() !== ""
            ? calcFinalPrice(form.offer_price, form.iva_percentage)
            : ""
        }
      />

      <ProductVariantsEditor
        variants={variants}
        selectedVariantType={selectedVariantType}
        defaultIva={Number(form.iva_percentage) || 15}
        onAdd={handleAddVariant}
        onUpdate={handleUpdateVariant}
        onRemove={handleRemoveVariant}
      />

      <ProductImagesUploader
        existingImages={existingImages}
        deleteImageIds={deleteImageIds}
        newImages={newImages}
        primaryImageKey={primaryImageKey}
        onAddImages={handleAddImages}
        onRemoveExistingImage={handleRemoveExistingImage}
        onRestoreExistingImage={handleRestoreExistingImage}
        onRemoveNewImage={handleRemoveNewImage}
        onPrimaryChange={setPrimaryImageKey}
      />

      <Paper
        sx={{
          borderRadius: "18px",
          border: "1px solid rgba(148,163,184,0.2)",
          boxShadow: "0 16px 36px rgba(15,23,42,0.05)",
          p: { xs: 2, md: 2.4 },
          bgcolor: "#ffffff",
        }}
      >
        <Stack direction="row" justifyContent="flex-end">
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || loadingCatalog}
            sx={{
              height: 38,
              px: 1.8,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              bgcolor: "#0f172a",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#111827",
                boxShadow: "none",
              },
            }}
          >
            {loading
              ? product
                ? "Guardando..."
                : "Creando..."
              : product
              ? "Guardar cambios"
              : "Crear producto"}
          </Button>
        </Stack>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.type}
          variant="filled"
          sx={{
            borderRadius: "12px",
            bgcolor:
              snackbar.type === "error"
                ? "rgba(220,38,38,0.12)"
                : "rgba(34,197,94,0.12)",
            color:
              snackbar.type === "error"
                ? "#dc2626"
                : "#166534",
            fontWeight: 600,
            "& .MuiAlert-icon": {
              color:
                snackbar.type === "error"
                  ? "#dc2626"
                  : "#166534",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}